import { orgAPI } from "@/api/api";
import { IntegrationConnection } from "@cohesive/api-client";
import { GithubProjectItemEntity } from "@cohesive/api-entities";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { buildGenericById, toGenericArray } from "../../genericProjection";

interface UseGitHubProjectItemsParams {
  connection: IntegrationConnection;
  projectId: string;
  contentType?: "Issue" | "PullRequest" | "DraftIssue";
  enabled?: boolean;
}

export function useGitHubProjectItems({
  connection,
  projectId,
  contentType,
  enabled = true,
}: UseGitHubProjectItemsParams) {
  const infiniteQuery = useInfiniteQuery({
    queryKey: [
      "github-project-items-list",
      connection?.id || "",
      projectId,
      contentType,
    ],
    queryFn: ({ pageParam }) => {
      const input: any = {
        project_id: projectId,
        first: 50,
      };

      // pageParam is the cursor string, not an object
      if (pageParam && typeof pageParam === "string") {
        input.after = pageParam;
      }
      if (contentType) {
        input.content_type = contentType;
      }

      return orgAPI.toolbox.execute<GithubProjectItemEntity[]>({
        integration_connection_id: connection?.id || "",
        action_name: "github-project-items-list",
        action_input: input,
        // Typed project items drive the rich row UI; their generic projections
        // ride along in data_generic and are what get placed on the canvas.
        entity_format: "both",
      });
    },
    getNextPageParam: (lastPage) => {
      // The backend returns: { pagination: { next: { after: "cursor" } } }
      // We need to extract just the cursor string
      const nextData = (lastPage as any)?.pagination?.next;
      if (nextData && typeof nextData === "object" && nextData.after) {
        return nextData.after; // Return just the cursor string
      }
      return undefined;
    },
    initialPageParam: undefined,
    enabled: enabled && !!connection?.id && !!projectId,
    staleTime: 1000 * 60,
  });

  const data = useMemo(() => {
    if (!infiniteQuery.data) return undefined;
    return {
      data: infiniteQuery.data.pages.flatMap((page) => page?.data || []),
    };
  }, [infiniteQuery.data]);

  // id→generic lookup for placement. The flattened `data` above drops
  // data_generic, so expose the map separately rather than reshaping `data`.
  const genericById = useMemo(
    () =>
      buildGenericById(
        infiniteQuery.data?.pages.flatMap((page) =>
          toGenericArray(page?.data_generic),
        ) ?? [],
      ),
    [infiniteQuery.data],
  );

  return {
    ...infiniteQuery,
    data,
    genericById,
  };
}
